import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/ui/design-system/components';
import {
    useCountryParam,
    useCurrentCountry,
    useFetchCountries,
} from 'utils/hooks';
import { Button } from '@/ui/design-system/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { mixpanelTrack } from 'utils/app';
import { useHistory } from 'react-router-dom';
import useURLSearchParams from 'utils/hooks/useURLSearchParams';

const COUNTRY_FLAGS = {
    US: '\u{1F1FA}\u{1F1F8}',
    GB: '\u{1F1EC}\u{1F1E7}',
    FR: '\u{1F1EB}\u{1F1F7}',
    KR: '\u{1F1F0}\u{1F1F7}',
    JP: '\u{1F1EF}\u{1F1F5}',
    GLOBAL: '\u{1F30D}',
};

function CountrySelect({
    mixpanelContext = 'CountrySelect',
    handleMixpanelEvent = ({ name }) => ({ countryName: name }),
}) {
    const { data: countries, isLoading } = useFetchCountries();
    const history = useHistory();
    const searchParams = useURLSearchParams();
    const { currentCountryParam, mutateToWithCountryParam } = useCountryParam();
    const currentCountry = useCurrentCountry();

    const currentValue = currentCountry?.text_id || currentCountryParam || '';

    const handleValueChange = (textId) => {
        const country = countries?.find((c) => c.text_id === textId);
        if (!country) return;

        mixpanelTrack('Change', {
            ...handleMixpanelEvent(country),
            context: mixpanelContext,
        });

        const hasRelatedCategoriesParams = Array.from(searchParams.keys()).some(
            (key) => key.startsWith('relatedCategories.'),
        );

        if (hasRelatedCategoriesParams) {
            const nextParams = new URLSearchParams();
            searchParams.forEach((value, key) => {
                if (key.startsWith('relatedCategories.')) {
                    nextParams.set(key, value);
                }
            });
            nextParams.set('country', textId);
            history.push(
                mutateToWithCountryParam({
                    search: `?${nextParams.toString()}`,
                }),
            );
            return;
        }

        history.push(
            mutateToWithCountryParam({
                search: `?country=${textId}`,
            }),
        );
    };

    const getFlag = (country) =>
        COUNTRY_FLAGS[country.code] ||
        COUNTRY_FLAGS[country.text_id] ||
        COUNTRY_FLAGS[country.name] ||
        '';

    const displayCode = (code) => (code === 'GB' ? 'UK' : code);

    const currentLabel = currentCountry
        ? `${getFlag(currentCountry)} ${displayCode(currentCountry.code) || currentCountry.name} Market`
        : currentCountryParam
          ? `${currentCountryParam} Market`
          : 'Select Market';

    return (
        <Select
            value={currentValue}
            onValueChange={handleValueChange}
            disabled={isLoading}
        >
            <SelectTrigger asChild>
                <Button
                    id="market-menu"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={isLoading}
                >
                    <span>{currentLabel}</span>
                    <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                </Button>
            </SelectTrigger>
            <SelectContent>
                {(countries || []).map((country) => (
                    <SelectItem key={country.text_id} value={country.text_id}>
                        <span className="flex items-center gap-2">
                            <span>{getFlag(country)}</span>
                            <span>{country.name}</span>
                        </span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

export default CountrySelect;
